// Renders V3 tool-owned questions while keeping registry wiring separate.
import {
    Fragment,
    type ReactNode,
    useContext,
    useId,
    useRef,
    useState,
} from 'react';

import { type VariableTextEditorActions } from '@apps/web/components/shared/messages/variable_text_editor';
import { isUuid } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import {
    type QuestionOption,
    type QuestionStatus,
    type SafeQuestionPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import { type SafeRenderContext } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CardTransition } from '@apps/web/features/phoebe_chat_v3/ui/primitives/card_transition';
import {
    CardActionButton,
    CommunicationActions,
    CommunicationCard,
    CommunicationField,
    CommunicationFields,
    CommunicationReceipt,
    RecipientPill,
    VariableButton,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/communication_card';
import { QuestionAnswerCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/question_answer_card';
import {
    QuestionComposerPanel,
    QuestionPanelOption,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/question_composer_panel';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import {
    denormalizeFirstNameToken,
    normalizeCaregiverTemplateTokens,
    SMS_MESSAGE_VARIABLES,
    SmsMessageEditor,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/sms_message_editor';
import { QuestionDockContext } from '@apps/web/features/phoebe_chat_v3/ui/question_dock';
import { cn } from '@apps/web/lib/utils';

function questionStatusCopy(status: QuestionStatus): string {
    switch (status) {
        case 'waiting':
            return 'Awaiting response';
        case 'answered':
            return 'Answered';
        case 'cancelled':
        case 'expired':
            // Skip and a superseding message both read as the user
            // skipping the question.
            return 'Skipped';
        default: {
            const exhaustiveStatus: never = status;
            return exhaustiveStatus;
        }
    }
}

function questionOptions(
    presentation: SafeQuestionPresentation,
    choiceValues: ReadonlyArray<string> | null,
): ReadonlyArray<QuestionOption> {
    let options: ReadonlyArray<QuestionOption>;
    if (presentation.input_type === 'yes_no') {
        options = [
            { value: 'yes', label: 'Yes', description: null },
            { value: 'no', label: 'No', description: null },
        ];
    } else if (
        presentation.presentation?.type === 'actions' ||
        presentation.presentation?.type === 'message_preview'
    ) {
        options = presentation.presentation.actions.map((action, index) => {
            const choice = presentation.choices?.[index];
            const value = choice?.label ?? action.value ?? '';
            const description = choice?.description ?? null;
            // Stored actions keep their button label for undescribed choices.
            return description === null && action.label !== null
                ? { value, label: action.label, description }
                : { value, label: value, description };
        });
    } else if (presentation.presentation?.type === 'list') {
        options = presentation.presentation.items.map((item) => ({
            value: item.id,
            label: item.title,
            description: item.detail,
        }));
    } else {
        options =
            presentation.choices?.map((choice) => ({
                value: choice.label,
                label: choice.label,
                description: choice.description,
            })) ?? [];
    }
    const valued =
        choiceValues === null || choiceValues.length !== options.length
            ? options
            : options.map((option, index) => ({
                  ...option,
                  value: choiceValues[index] ?? option.value,
              }));
    // A list or action presentation carries readable titles of its own.
    // Plain choices do not: the tool asks the agent for a row's stable id
    // as the label, and the display sanitizer blanks ids to one
    // placeholder, so an id-labeled plain choice reads by its description.
    if (presentation.presentation !== null) {
        return valued;
    }
    return valued.map((option) =>
        isUuid(option.value) && option.description
            ? { ...option, label: option.description, description: null }
            : option,
    );
}

function questionAnswerCopy(
    answer: string | ReadonlyArray<string> | null,
    options: ReadonlyArray<QuestionOption>,
): string | null {
    if (answer === null) {
        return null;
    }
    const labelByValue = new Map(
        options.map((option) => [option.value, option.label]),
    );
    const values = typeof answer === 'string' ? [answer] : answer;
    return values.map((value) => labelByValue.get(value) ?? value).join(', ');
}

function QuestionTransitionFrame({
    callId,
    status,
    children,
}: Readonly<{
    readonly callId: string;
    readonly status: QuestionStatus;
    readonly children: ReactNode;
}>): ReactNode {
    // Dock and transcript have different geometry. Sharing a layout id
    // scales the entire question into its receipt, including the text.
    return (
        <div data-question-call-id={callId}>
            <CardTransition transitionKey={status}>{children}</CardTransition>
        </div>
    );
}

export function LegacyControlledQuestionView({
    callId,
    requestSubmit,
    status,
}: Readonly<{
    readonly callId: string;
    readonly presentation: null;
    readonly requestSubmit: SafeRenderContext['requestQuestionSubmit'];
    readonly status: QuestionStatus;
}>): ReactNode {
    const [submitting, setSubmitting] = useState(false);

    function cancel(): void {
        if (submitting || status !== 'waiting') {
            return;
        }
        setSubmitting(true);
        void requestSubmit(callId, {
            kind: 'question_response',
            responseKind: 'cancel',
            callId,
        }).then((outcome) => {
            if (outcome !== 'accepted' && outcome !== 'already_resolved') {
                setSubmitting(false);
            }
        });
    }

    return (
        <AssistantCardFrame statusText={questionStatusCopy(status)}>
            <QuestionAnswerCard
                question="Phoebe asked a question that could not be displayed."
                choices={[]}
                selectedValues={new Set()}
                otherText={null}
                onSelectChoice={() => undefined}
                onSkip={cancel}
                onContinue={() => undefined}
                disabled={submitting}
                status={status}
            />
        </AssistantCardFrame>
    );
}

export function AssistantCardFrame({
    children,
    statusText,
}: Readonly<{
    children: ReactNode;
    statusText?: string;
}>): ReactNode {
    return (
        <div className="min-w-0">
            {statusText !== undefined && (
                <span className="sr-only">{statusText}</span>
            )}
            {children}
        </div>
    );
}

function ControlledMessageQuestionView({
    callId,
    choiceValues,
    presentation,
    requestSubmit,
    resolvedAnswer,
    status,
}: Readonly<{
    readonly callId: string;
    readonly choiceValues: ReadonlyArray<string> | null;
    readonly presentation: SafeQuestionPresentation & {
        readonly presentation: Extract<
            NonNullable<SafeQuestionPresentation['presentation']>,
            { readonly type: 'message_preview' }
        >;
    };
    readonly requestSubmit: SafeRenderContext['requestQuestionSubmit'];
    readonly resolvedAnswer: string | ReadonlyArray<string> | null;
    readonly status: QuestionStatus;
}>): ReactNode {
    const dock = useContext(QuestionDockContext);
    const [submitting, setSubmitting] = useState(false);
    const enhancement = presentation.presentation;
    const recipients = enhancement.recipients ?? [];
    const [activeRecipientIndexes, setActiveRecipientIndexes] = useState(() =>
        recipients.map((_, index) => index),
    );
    const activeRecipients = activeRecipientIndexes.map(
        (index) => recipients[index] ?? '',
    );
    // The card edits normalized caregiver variables as pills; the legacy
    // double-brace wire spelling is restored on submit.
    const [message, setMessage] = useState(() =>
        normalizeCaregiverTemplateTokens(enhancement.message ?? ''),
    );
    const messageEditorActions = useRef<VariableTextEditorActions>(null);
    const waiting = status === 'waiting';
    // A message edit travels as messagePreviewEdit, whose wire contract
    // requires named recipients — a count-only presentation cannot
    // carry an edit, so its message stays read-only.
    const messageEditable = recipients.length > 0;
    const submittedMessage = denormalizeFirstNameToken(message);
    const edited =
        message !==
            normalizeCaregiverTemplateTokens(enhancement.message ?? '') ||
        activeRecipients.length !== recipients.length ||
        activeRecipientIndexes.some(
            (recipientIndex, index) => recipientIndex !== index,
        );
    const options = questionOptions(presentation, choiceValues);

    function submit(value: string): Promise<boolean> {
        if (submitting || !waiting) {
            return Promise.resolve(false);
        }
        setSubmitting(true);
        return requestSubmit(callId, {
            kind: 'question_response',
            responseKind: 'answer',
            callId,
            answer: value,
            ...(edited
                ? {
                      messagePreviewEdit: {
                          recipients: activeRecipients,
                          message: submittedMessage,
                      },
                  }
                : {}),
        }).then((outcome) => {
            const accepted =
                outcome === 'accepted' || outcome === 'already_resolved';
            if (!accepted) {
                setSubmitting(false);
            }
            return accepted;
        });
    }

    // A typed reply answers in the coordinator's own words, without the
    // local draft edit: the answer contract only carries an edit with a
    // declared action, and a worded reply supersedes the staged draft —
    // the agent re-presents an updated preview before any send.
    function submitComposerText(text: string): Promise<boolean> {
        const trimmed = text.trim();
        if (trimmed === '' || submitting || !waiting) {
            return Promise.resolve(false);
        }
        setSubmitting(true);
        return requestSubmit(callId, {
            kind: 'question_response',
            responseKind: 'answer',
            callId,
            answer: trimmed,
        }).then((outcome) => {
            const accepted =
                outcome === 'accepted' || outcome === 'already_resolved';
            if (!accepted) {
                setSubmitting(false);
            }
            return accepted;
        });
    }

    const answerCopy = questionAnswerCopy(resolvedAnswer, options);
    const card = (
        <CommunicationCard
            tone={
                status === 'answered'
                    ? 'approved'
                    : status === 'waiting'
                      ? 'editing'
                      : 'rejected'
            }
        >
            {waiting ? (
                <Fragment>
                    <CommunicationFields>
                        <CommunicationField label="To:" inline={true}>
                            {recipients.length > 0 ? (
                                activeRecipientIndexes.map((recipientIndex) => (
                                    <RecipientPill
                                        key={recipientIndex}
                                        name={recipients[recipientIndex] ?? ''}
                                        onRemove={() => {
                                            setActiveRecipientIndexes(
                                                (current) =>
                                                    current.filter(
                                                        (index) =>
                                                            index !==
                                                            recipientIndex,
                                                    ),
                                            );
                                        }}
                                    />
                                ))
                            ) : (
                                <span className="text-muted-foreground text-v3-chat-meta">
                                    {enhancement.recipient_count ?? 0}{' '}
                                    {enhancement.recipient_noun ?? 'recipients'}
                                </span>
                            )}
                            {recipients
                                .map((recipient, index) => ({
                                    index,
                                    recipient,
                                }))
                                .filter(
                                    ({ index }) =>
                                        !activeRecipientIndexes.includes(index),
                                )
                                .map(({ index, recipient }) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() =>
                                            setActiveRecipientIndexes(
                                                (current) => [
                                                    ...current,
                                                    index,
                                                ],
                                            )
                                        }
                                        className="bg-card text-foreground/80 hover:bg-accent text-v3-chat-meta inline-flex min-h-6 items-center rounded-md border px-[7px] py-1 font-medium"
                                    >
                                        Restore{' '}
                                        <ResolvedReferenceText
                                            text={recipient}
                                        />
                                    </button>
                                ))}
                        </CommunicationField>
                        <CommunicationField label="Message:">
                            <SmsMessageEditor
                                ariaLabel="Message"
                                value={message}
                                removePillsOnDragOut={true}
                                snapVariableDropsToWords={true}
                                {...(messageEditable
                                    ? { onChange: setMessage }
                                    : {})}
                                actionRef={messageEditorActions}
                            />
                            {messageEditable && (
                                <div
                                    className="mt-[9px] flex flex-wrap items-center gap-1.5"
                                    aria-label="Message variables"
                                >
                                    {SMS_MESSAGE_VARIABLES.map((variable) => (
                                        <VariableButton
                                            key={variable.token}
                                            variable={`{${variable.token}}`}
                                            onClick={() =>
                                                messageEditorActions.current?.insertVariable(
                                                    variable.token,
                                                )
                                            }
                                        >
                                            {variable.label}
                                        </VariableButton>
                                    ))}
                                </div>
                            )}
                        </CommunicationField>
                    </CommunicationFields>
                    <CommunicationActions>
                        {enhancement.actions.map((action, index) => (
                            <CardActionButton
                                key={index}
                                variant={
                                    action.style === 'primary'
                                        ? 'primary'
                                        : 'secondary'
                                }
                                disabled={
                                    submitting ||
                                    message.trim().length === 0 ||
                                    // The server rejects preview edits
                                    // over 1,600 characters; an
                                    // untouched draft submits no edit.
                                    (edited &&
                                        submittedMessage.length > 1_600) ||
                                    (recipients.length > 0 &&
                                        activeRecipients.length === 0)
                                }
                                onClick={() =>
                                    void submit(options[index]?.value ?? '')
                                }
                            >
                                <ResolvedReferenceText
                                    text={options[index]?.label ?? ''}
                                />
                            </CardActionButton>
                        ))}
                    </CommunicationActions>
                </Fragment>
            ) : (
                <CommunicationReceipt
                    tone={status === 'answered' ? 'approved' : 'rejected'}
                    status={status === 'answered' ? 'succeeded' : 'skipped'}
                    title={presentation.question}
                    detail={
                        status === 'answered'
                            ? (answerCopy ?? undefined)
                            : undefined
                    }
                />
            )}
        </CommunicationCard>
    );
    if (dock !== null && waiting) {
        // The draft review keeps its editor and action buttons, but wraps
        // the live composer so the chat stays open for a typed reply.
        return (
            <QuestionTransitionFrame callId={callId} status={status}>
                <span className="sr-only">{questionStatusCopy(status)}</span>
                <QuestionComposerPanel
                    question={presentation.question}
                    onDismiss={dock.dismiss}
                    composer={dock.renderComposer({
                        onSendText: submitComposerText,
                    })}
                >
                    <div className="px-1 pb-1.5">{card}</div>
                </QuestionComposerPanel>
            </QuestionTransitionFrame>
        );
    }
    return (
        <QuestionTransitionFrame callId={callId} status={status}>
            {card}
        </QuestionTransitionFrame>
    );
}

function DockedQuestionOptions({
    question,
    options,
    multiple,
    selectedValues,
    submitting,
    submissionKind,
    onToggleChoice,
    onSelectAndSubmit,
    onSubmitSelections,
}: Readonly<{
    readonly question: string;
    readonly options: ReadonlyArray<QuestionOption>;
    readonly multiple: boolean;
    readonly selectedValues: ReadonlySet<string>;
    readonly submitting: boolean;
    readonly submissionKind: 'answer' | 'cancel' | null;
    readonly onToggleChoice: (choiceId: string) => void;
    readonly onSelectAndSubmit: (choiceId: string) => void;
    readonly onSubmitSelections: () => void;
}>): ReactNode {
    const questionLabelId = useId();
    if (options.length === 0) {
        return null;
    }
    return (
        <div
            role="group"
            aria-labelledby={questionLabelId}
            className="flex flex-col gap-0.5 px-1 pb-1.5"
        >
            <span id={questionLabelId} className="sr-only">
                <ResolvedReferenceText text={question} />
            </span>
            {options.map((option, index) => {
                const choiceId = String(index);
                const selected = selectedValues.has(choiceId);
                return (
                    <QuestionPanelOption
                        key={choiceId}
                        letter={String.fromCharCode(65 + (index % 26))}
                        label={option.label}
                        {...(option.description === null
                            ? {}
                            : { description: option.description })}
                        multiple={multiple}
                        selected={selected}
                        dimmed={submitting && !selected}
                        disabled={submitting}
                        onClick={() =>
                            multiple
                                ? onToggleChoice(choiceId)
                                : onSelectAndSubmit(choiceId)
                        }
                    />
                );
            })}
            {multiple && (
                <div className="flex justify-end px-3 pt-1 pb-1.5">
                    <button
                        type="button"
                        aria-live="polite"
                        disabled={submitting || selectedValues.size === 0}
                        onClick={onSubmitSelections}
                        className={cn(
                            'focus-visible:ring-ring/50 min-h-[33px] rounded-md border px-3 py-[7px] text-v3-chat font-semibold text-white outline-none',
                            'transition-[background-color,border-color,opacity] duration-150 motion-reduce:transition-none',
                            'focus-visible:ring-[3px]',
                            submissionKind === 'answer'
                                ? 'border-agent-positive bg-agent-positive'
                                : 'border-agent-action bg-agent-action hover:border-agent-action-strong hover:bg-agent-action-strong disabled:opacity-[0.42]',
                        )}
                    >
                        {submissionKind === 'answer' ? 'Sending…' : 'Continue'}
                    </button>
                </div>
            )}
        </div>
    );
}

export function ControlledQuestionView({
    callId,
    choiceValues,
    presentation,
    requestSubmit,
    resolvedAnswer,
    status,
}: Readonly<{
    readonly callId: string;
    readonly choiceValues: ReadonlyArray<string> | null;
    readonly presentation: SafeQuestionPresentation | null;
    readonly requestSubmit: SafeRenderContext['requestQuestionSubmit'];
    readonly resolvedAnswer: string | ReadonlyArray<string> | null;
    readonly status: QuestionStatus;
}>): ReactNode {
    const dock = useContext(QuestionDockContext);
    const [answerText, setAnswerText] = useState('');
    const [selectedValues, setSelectedValues] = useState<ReadonlySet<string>>(
        new Set(),
    );
    const [submissionKind, setSubmissionKind] = useState<
        'answer' | 'cancel' | null
    >(null);
    const submitting = submissionKind !== null;
    const waiting = status === 'waiting';

    function submitAnswer(
        answer: string | ReadonlyArray<string>,
    ): Promise<boolean> {
        setSubmissionKind('answer');
        return requestSubmit(callId, {
            kind: 'question_response',
            responseKind: 'answer',
            callId,
            answer: typeof answer === 'string' ? answer : [...answer],
        }).then((outcome) => {
            const accepted =
                outcome === 'accepted' || outcome === 'already_resolved';
            if (!accepted) {
                setSubmissionKind(null);
            }
            return accepted;
        });
    }

    // The docked composer always accepts a typed reply, even when the
    // transcript card would hide its free-text row (yes/no questions): the
    // answer contract carries any string, and blocking typing is exactly
    // what the dock exists to avoid.
    async function submitComposerText(
        text: string,
        selections: ReadonlyArray<string>,
        multiple: boolean,
    ): Promise<boolean> {
        if (submitting || !waiting) {
            return false;
        }
        const trimmed = text.trim();
        if (trimmed === '') {
            return false;
        }
        if (multiple) {
            const merged = [...selections];
            if (!merged.includes(trimmed)) {
                merged.push(trimmed);
            }
            return submitAnswer(merged);
        }
        return submitAnswer(trimmed);
    }

    if (presentation === null) {
        if (dock !== null && waiting) {
            return (
                <QuestionTransitionFrame callId={callId} status={status}>
                    <span className="sr-only">
                        {questionStatusCopy(status)}
                    </span>
                    <QuestionComposerPanel
                        question="Phoebe asked a question that could not be displayed."
                        onDismiss={dock.dismiss}
                        composer={dock.renderComposer({
                            onSendText: (text) =>
                                submitComposerText(text, [], false),
                        })}
                    />
                </QuestionTransitionFrame>
            );
        }
        return (
            <QuestionTransitionFrame callId={callId} status={status}>
                <LegacyControlledQuestionView
                    callId={callId}
                    presentation={presentation}
                    requestSubmit={requestSubmit}
                    status={status}
                />
            </QuestionTransitionFrame>
        );
    }
    if (presentation.presentation?.type === 'message_preview') {
        return (
            <ControlledMessageQuestionView
                callId={callId}
                choiceValues={choiceValues}
                presentation={{
                    ...presentation,
                    presentation: presentation.presentation,
                }}
                requestSubmit={requestSubmit}
                resolvedAnswer={resolvedAnswer}
                status={status}
            />
        );
    }

    const options = questionOptions(presentation, choiceValues);
    const multiple = presentation.input_type === 'multi_choice';
    const showOther =
        presentation.input_type === 'text' ||
        presentation.input_type === 'multi_choice' ||
        (presentation.allow_other &&
            presentation.input_type === 'single_choice');

    function orderedSelectionValues(): Array<string> {
        return options
            .map((option, index) => ({ option, choiceId: String(index) }))
            .filter(({ choiceId }) => selectedValues.has(choiceId))
            .map(({ option }) => option.value);
    }

    function selectAndSubmit(choiceId: string): void {
        if (submitting || !waiting) {
            return;
        }
        const answer = options[Number(choiceId)]?.value ?? choiceId;
        setSelectedValues(new Set([choiceId]));
        void submitAnswer(answer);
    }

    function toggleChoice(choiceId: string): void {
        if (submitting || !waiting) {
            return;
        }
        const next = new Set(selectedValues);
        if (next.has(choiceId)) {
            next.delete(choiceId);
        } else {
            next.add(choiceId);
        }
        setSelectedValues(next);
    }

    function submit(): void {
        if (submitting || !waiting) {
            return;
        }
        const text = answerText.trim();
        const orderedSelections = orderedSelectionValues();
        const multiAnswers = [...orderedSelections];
        if (multiple && text !== '' && !multiAnswers.includes(text)) {
            multiAnswers.push(text);
        }
        const answer = multiple
            ? multiAnswers
            : text !== ''
              ? text
              : orderedSelections[0];
        if (
            answer === undefined ||
            (Array.isArray(answer) && answer.length === 0)
        ) {
            return;
        }
        void submitAnswer(answer);
    }

    function cancel(): void {
        if (submitting || !waiting) {
            return;
        }
        setSubmissionKind('cancel');
        void requestSubmit(callId, {
            kind: 'question_response',
            responseKind: 'cancel',
            callId,
        }).then((outcome) => {
            if (outcome !== 'accepted' && outcome !== 'already_resolved') {
                setSubmissionKind(null);
            }
        });
    }

    if (dock !== null && waiting) {
        return (
            <QuestionTransitionFrame callId={callId} status={status}>
                <span className="sr-only">{questionStatusCopy(status)}</span>
                <QuestionComposerPanel
                    question={presentation.question}
                    onDismiss={dock.dismiss}
                    composer={dock.renderComposer({
                        onSendText: (text) =>
                            submitComposerText(
                                text,
                                orderedSelectionValues(),
                                multiple,
                            ),
                        ...(presentation.placeholder === null
                            ? {}
                            : { placeholder: presentation.placeholder }),
                    })}
                >
                    <DockedQuestionOptions
                        question={presentation.question}
                        options={options}
                        multiple={multiple}
                        selectedValues={selectedValues}
                        submitting={submitting}
                        submissionKind={submissionKind}
                        onToggleChoice={toggleChoice}
                        onSelectAndSubmit={selectAndSubmit}
                        onSubmitSelections={() => {
                            const selections = orderedSelectionValues();
                            if (submitting || selections.length === 0) {
                                return;
                            }
                            void submitAnswer(selections);
                        }}
                    />
                </QuestionComposerPanel>
            </QuestionTransitionFrame>
        );
    }

    return (
        <QuestionTransitionFrame callId={callId} status={status}>
            {status === 'waiting' && (
                <span className="sr-only">{questionStatusCopy(status)}</span>
            )}
            <QuestionAnswerCard
                question={presentation.question}
                choices={options.map((option, index) => ({
                    id: String(index),
                    value: option.value,
                    label: option.label,
                    ...(option.description === null
                        ? {}
                        : { description: option.description }),
                }))}
                selectedValues={selectedValues}
                multiple={multiple}
                onSelectChoice={(choiceId) => {
                    // Single-answer questions submit on the click itself; a
                    // select-then-confirm step only remains for multi-choice.
                    if (!multiple) {
                        setAnswerText('');
                        selectAndSubmit(choiceId);
                        return;
                    }
                    toggleChoice(choiceId);
                }}
                otherText={showOther ? answerText : null}
                onOtherTextChange={
                    showOther
                        ? (text) => {
                              setAnswerText(text);
                              if (!multiple && text.trim() !== '') {
                                  setSelectedValues(new Set());
                              }
                          }
                        : undefined
                }
                otherPlaceholder={presentation.placeholder ?? undefined}
                onSkip={cancel}
                onContinue={submit}
                disabled={submitting}
                submitting={waiting && submissionKind === 'answer'}
                status={status}
                resolvedAnswer={questionAnswerCopy(resolvedAnswer, options)}
            />
        </QuestionTransitionFrame>
    );
}
